/* ==========================================================================
   MAISON MIQU — Cookie consent
   A banner and a preferences dialog connected to Shopify's Customer Privacy
   API (https://shopify.dev/docs/api/customer-privacy).

   Consent is only ever recorded from a visitor's own click, as the API
   requires. The sale-of-data signal is deliberately never set here: Shopify
   specifies that opt-out must be started by the customer, not offered from a
   consent banner.
   ========================================================================== */

(() => {
  const root = document.querySelector('[data-cookie-consent]');
  if (!root || root.dataset.consentReady === 'true') return;
  root.dataset.consentReady = 'true';

  const banner = root.querySelector('[data-consent-banner]');
  const dialog = root.querySelector('[data-consent-dialog]');
  if (!banner || !dialog) return;

  const PURPOSES = ['preferences', 'analytics', 'marketing'];
  const TRANSITION_MS = 600;

  const toggles = Object.fromEntries(
    PURPOSES.map((purpose) => [purpose, root.querySelector(`[data-consent-purpose="${purpose}"]`)])
  );
  const inDesignMode = root.hasAttribute('data-design-mode');
  const onlyWhereRequired = root.dataset.scope === 'required';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let apiReady = false;
  let hideTimer = 0;
  /* The choice made on this page. Shopify can take a moment to reflect a new choice
     in currentVisitorConsent(), so the dialog uses this until the API catches up. */
  let lastRecorded = null;

  /* ---------- Shopify Customer Privacy API ---------- */

  /* Always read the live object, never a cached one. window.Shopify.customerPrivacy
     can start as a bare config stub, which Shopify's own storefront-banner.js replaces
     (it reassigns the global when it has one key or fewer). A reference taken early can
     be that stub, and a choice recorded through it is lost — proven 2026-09-15 by
     forcing the condition: cached reference 0/3 recorded, live read 3/3. */
  const privacy = () => {
    const api = window.Shopify && window.Shopify.customerPrivacy;
    return api && typeof api.setTrackingConsent === 'function' ? api : null;
  };

  /* Resolves true once the API has genuinely loaded — never on the mere existence of
     the object, which may still be that stub. */
  const loadPrivacyApi = () =>
    new Promise((resolve) => {
      const shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') {
        resolve(Boolean(privacy()));
        return;
      }
      /* If the loader never calls back (a blocked or failed script), fall back to
         whatever has genuinely loaded by then instead of hanging silently. */
      const fallback = window.setTimeout(() => resolve(Boolean(privacy())), 10000);
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        window.clearTimeout(fallback);
        resolve(!error && Boolean(privacy()));
      });
    });

  const visitorConsent = () => {
    const api = privacy();
    try {
      return (api && api.currentVisitorConsent()) || {};
    } catch (error) {
      return {};
    }
  };

  /* A decision is any purpose answered 'yes' or 'no'; '' means never asked. */
  const hasDecided = () => {
    const consent = visitorConsent();
    return PURPOSES.some((purpose) => consent[purpose] === 'yes' || consent[purpose] === 'no');
  };

  /* Shopify only loads its own banner script while that banner is switched on.
     In the regions it covers it asks the visitor itself, so this banner stands
     aside there rather than stacking a second one on top. */
  const shopifyBannerActive = () =>
    Boolean(document.querySelector('script[src*="privacy-banner/storefront-banner"]'));

  const shouldAsk = () => {
    if (inDesignMode) return true;
    if (!apiReady || hasDecided()) return false;

    let regulated = false;
    try {
      const api = privacy();
      regulated = Boolean(api && api.shouldShowBanner());
    } catch (error) {
      regulated = false;
    }

    if (regulated && shopifyBannerActive()) return false;
    return onlyWhereRequired ? regulated : true;
  };

  const record = (choices) => {
    /* Never store the merchant's own consent from inside the theme editor. */
    if (inDesignMode) return;

    const api = privacy();
    if (!api) {
      console.warn('[MIQU consent] Customer Privacy API unavailable; the choice was not recorded.');
      return;
    }

    try {
      api.setTrackingConsent(choices, () => {});
    } catch (error) {
      console.warn('[MIQU consent] The choice could not be recorded.', error);
    }
  };

  /* ---------- Banner: `hidden` decides presence, the class only the fade ---------- */

  const showBanner = () => {
    window.clearTimeout(hideTimer);
    banner.hidden = false;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => banner.classList.add('is-visible'));
    });
  };

  const hideBanner = () => {
    if (banner.hidden) return;
    banner.classList.remove('is-visible');
    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(
      () => {
        banner.hidden = true;
      },
      reducedMotion.matches ? 0 : TRANSITION_MS
    );
  };

  /* ---------- Preferences dialog ---------- */

  const syncToggles = () => {
    const consent = visitorConsent();
    PURPOSES.forEach((purpose) => {
      if (!toggles[purpose]) return;
      const answered = consent[purpose] === 'yes' || consent[purpose] === 'no';
      toggles[purpose].checked = answered || !lastRecorded
        ? consent[purpose] === 'yes'
        : Boolean(lastRecorded[purpose]);
    });
  };

  const openPreferences = () => {
    syncToggles();
    if (dialog.open) return;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('mq-consent-open');
  };

  const closePreferences = () => {
    if (dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    document.documentElement.classList.remove('mq-consent-open');
  };

  /* Escape closes a native dialog without going through closePreferences. */
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('mq-consent-open');
  });

  /* A click that lands on the dialog element itself is a click on the backdrop. */
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closePreferences();
  });

  /* ---------- Decisions ---------- */

  const everyPurpose = (value) => Object.fromEntries(PURPOSES.map((purpose) => [purpose, value]));

  const decide = (choices) => {
    lastRecorded = choices;
    record(choices);
    closePreferences();
    hideBanner();
  };

  root.addEventListener('click', (event) => {
    const control = event.target.closest('[data-consent-action]');
    if (!control) return;

    switch (control.dataset.consentAction) {
      case 'accept':
        decide(everyPurpose(true));
        break;
      case 'reject':
        decide(everyPurpose(false));
        break;
      case 'save':
        decide(
          Object.fromEntries(
            PURPOSES.map((purpose) => [purpose, Boolean(toggles[purpose] && toggles[purpose].checked)])
          )
        );
        break;
      case 'preferences':
        openPreferences();
        break;
      case 'close':
        closePreferences();
        break;
      default:
        break;
    }
  });

  /* Any link to #cookie-preferences opens the dialog: the footer, the Cookie
     Policy page. A link to that anchor on a different page navigates normally,
     and the dialog opens on arrival. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('a[href*="#cookie-preferences"], [data-cookie-preferences]');
    if (!opener || root.contains(opener)) return;

    if (opener.tagName === 'A') {
      const destination = new URL(opener.getAttribute('href'), window.location.href);
      if (destination.pathname !== window.location.pathname) return;
    }

    event.preventDefault();
    openPreferences();
  });

  /* ---------- Start ---------- */

  loadPrivacyApi().then((ready) => {
    apiReady = ready;

    if (!ready && !inDesignMode) {
      console.warn('[MIQU consent] Customer Privacy API unavailable; the banner was not shown.');
    }

    if (shouldAsk()) showBanner();
    if (window.location.hash === '#cookie-preferences') openPreferences();
  });
})();
